/**
 * Sorts the mapping screen's rows into the four sections it shows, and counts what is ready.
 *
 * A row's section comes from what the server holds, never from what the advisor is in the middle of
 * choosing. Otherwise a row would jump to another section the moment its dropdown changed, away from
 * the pointer that changed it. It moves once the save lands.
 *
 * - `attention`: one choice from the advisor is needed. A number with no unit, a yes/no read from a
 *   field that is not one and has no rule, a mapped field the CRM no longer offers, or a field with
 *   candidates and nothing chosen.
 * - `matched`: Waterlily proposed a field and nothing else is needed but the confirmation.
 * - `confirmed`: in use, including the fields that fill themselves from related records.
 * - `notMapped`: nothing is read. Either the CRM has nothing that looks like it, or the advisor
 *   said to leave it.
 */
import type { Prospecting } from '@client/api/types';

/** The value a row's picker holds when no CRM field is chosen. */
export const UNMAPPED = '__unmapped__';

export type FieldMapGroupKey = 'attention' | 'matched' | 'confirmed' | 'notMapped';

export const FIELD_MAP_GROUP_ORDER: FieldMapGroupKey[] = ['attention', 'matched', 'confirmed', 'notMapped'];

export interface GroupFacts {
  status: Prospecting.FieldMapStatus | null;
  /** The saved mapping has a number with no unit, or a yes/no with no rule for a field that needs one. */
  savedNeedsMeaning: boolean;
  /** The saved CRM field is no longer in the CRM's field list. */
  sourceMissing: boolean;
  isDerived: boolean;
  hasCandidates: boolean;
}

export function groupOf(facts: GroupFacts): FieldMapGroupKey {
  if (facts.sourceMissing) return 'attention';
  if (facts.status === 'confirmed') return facts.savedNeedsMeaning ? 'attention' : 'confirmed';
  if (facts.status === 'suggested') return facts.savedNeedsMeaning ? 'attention' : 'matched';
  if (facts.isDerived) return 'confirmed';
  if (facts.status === 'rejected') return 'notMapped';
  return facts.hasCandidates ? 'attention' : 'notMapped';
}

export interface FieldMapGroup<Row> {
  key: FieldMapGroupKey;
  rows: Row[];
}

/** The sections that have rows, in the order they are shown. */
export function groupRows<Row extends { group: FieldMapGroupKey }>(rows: Row[]): FieldMapGroup<Row>[] {
  return FIELD_MAP_GROUP_ORDER.map(key => ({ key, rows: rows.filter(row => row.group === key) })).filter(
    group => group.rows.length > 0,
  );
}

export interface FieldMapSummary {
  ready: number;
  total: number;
  /** 0 to 100, for a progress bar. */
  percent: number;
}

export function summarise(rows: { group: FieldMapGroupKey }[]): FieldMapSummary {
  const ready = rows.filter(row => row.group === 'confirmed').length;
  const total = rows.length;
  return { ready, total, percent: total === 0 ? 0 : Math.round((ready / total) * 100) };
}

/** A yes/no read from a field that is not one has a rule when it says what means yes. */
export function hasYesRule(semantics: Prospecting.FieldSemantics | null | undefined): boolean {
  return Boolean(semantics?.presentMeansTrue || semantics?.truthyValues?.length);
}
